import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import axios from 'axios'
import { X, Search, Users, BookOpen, Check } from 'lucide-react'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Tabs, TabsList, TabsTrigger } from '../ui/tabs'
import { Avatar } from '../ui/avatar'
import LoadingSpinner from '../leevo_ui/LoadingSpinner/LoadingSpinner'
import useAuth from '../../utilities/hooks/useAuth'

function NewGroupConversationModal({ isOpen, onClose, onCreateGroupConversation }) {
  const { currentUser } = useAuth()
  const [activeTab, setActiveTab] = useState('by-class')
  const [title, setTitle] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [klasses, setKlasses] = useState([])
  const [selectedKlass, setSelectedKlass] = useState(null)
  const [messageableUsers, setMessageableUsers] = useState({ guardians: [], directors: [], staff: [] })
  const [selectedUserIds, setSelectedUserIds] = useState([])
  const [isLoadingKlasses, setIsLoadingKlasses] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isCreating, setIsCreating] = useState(false)

  useEffect(() => {
    if (isOpen) {
      fetchKlasses()
      fetchUsers()
    }
  }, [isOpen])

  useEffect(() => {
    if (selectedKlass && !title) {
      setTitle(selectedKlass.attributes.name)
    }
  }, [selectedKlass])

  const fetchKlasses = async () => {
    setIsLoadingKlasses(true)
    try {
      const response = await axios.get('/api/messageable_klasses')
      setKlasses(response.data.data || [])
    } catch (error) {
      console.error('Error fetching messageable classes:', error)
    } finally {
      setIsLoadingKlasses(false)
    }
  }

  const fetchUsers = async () => {
    setIsLoading(true)
    try {
      const response = await axios.get('/api/messageable_users')
      const { data } = response.data

      if (data?.facilities) {
        const directors = data.facilities.flatMap((f) => f.directors || [])
        const staff = data.facilities.flatMap((f) => f.staff || [])
        setMessageableUsers({ directors, staff, guardians: [] })
      } else {
        setMessageableUsers({
          guardians: data?.guardians || [],
          directors: data?.directors || [],
          staff: data?.staff || []
        })
      }
    } catch (error) {
      console.error('Error fetching messageable users:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleSelectKlass = (klass) => {
    if (selectedKlass?.id === klass.id) {
      setSelectedKlass(null)
      if (title === klass.attributes.name) setTitle('')
    } else {
      setSelectedKlass(klass)
    }
  }

  const handleToggleUser = (userId) => {
    setSelectedUserIds((prev) => (prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]))
  }

  const creatorFacilityId = currentUser?.facilitiesUsers?.[0]?.facility?.id || klasses[0]?.attributes?.facilityId

  const handleCreate = async () => {
    if (!canCreate) return
    setIsCreating(true)

    try {
      const payload = {
        title,
        facility_id: activeTab === 'by-class' ? selectedKlass?.attributes?.facilityId : creatorFacilityId,
        klass_id:
          activeTab === 'by-class' && selectedKlass?.attributes?.klassIds?.length === 1
            ? selectedKlass.attributes.klassIds[0]
            : null,
        klass_ids: activeTab === 'by-class' ? selectedKlass?.attributes?.klassIds || [] : [],
        participant_ids: activeTab === 'by-individual' ? selectedUserIds : []
      }

      await onCreateGroupConversation(payload)
      handleClose()
    } catch (error) {
      console.error('Error creating group conversation:', error)
    } finally {
      setIsCreating(false)
    }
  }

  const handleClose = () => {
    setTitle('')
    setSearchTerm('')
    setSelectedKlass(null)
    setSelectedUserIds([])
    setActiveTab('by-class')
    onClose()
  }

  const filteredKlasses = klasses.filter((k) => {
    if (!searchTerm) return true
    const name = k.attributes?.name?.toLowerCase() || ''
    const facility = k.attributes?.facilityName?.toLowerCase() || ''
    return name.includes(searchTerm.toLowerCase()) || facility.includes(searchTerm.toLowerCase())
  })

  const filterUsers = (userList) => {
    if (!searchTerm) return userList
    const term = searchTerm.toLowerCase()
    return userList.filter((u) => {
      const preferred = (u.attributes?.preferredName || '').toLowerCase()
      const full = (u.attributes?.fullName || '').toLowerCase()
      return preferred.includes(term) || full.includes(term)
    })
  }

  const canCreate =
    title.trim().length > 0 && (activeTab === 'by-class' ? selectedKlass !== null : selectedUserIds.length > 0)

  if (!isOpen) return null

  const renderUserRow = (user) => {
    const attrs = user.attributes || {}
    const isSelected = selectedUserIds.includes(user.id)

    return (
      <button
        key={user.id}
        onClick={() => handleToggleUser(user.id)}
        className={`w-full p-3 text-left transition-colors flex items-center gap-3 ${
          isSelected ? 'bg-purple-50' : 'hover:bg-gray-50'
        }`}
      >
        <Avatar className="size-9 flex-shrink-0">
          {attrs.cloudinaryPhotoPublicId ? (
            <img
              src={`https://res.cloudinary.com/${process.env.CLOUDINARY_CLOUD_NAME}/image/upload/c_fill,g_face,h_36,w_36/${attrs.cloudinaryPhotoPublicId}.jpg`}
              alt={attrs.preferredName}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-purple-200 flex items-center justify-center text-purple-700 font-semibold text-sm">
              {(attrs.preferredName || '?')[0].toUpperCase()}
            </div>
          )}
        </Avatar>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-gray-900 text-sm truncate">{attrs.preferredName}</p>
          {attrs.fullName && attrs.fullName !== attrs.preferredName && (
            <p className="text-xs text-gray-500 truncate">{attrs.fullName}</p>
          )}
        </div>
        {isSelected && <Check className="w-4 h-4 text-purple-600 flex-shrink-0" />}
      </button>
    )
  }

  const renderUserSection = (userList, label) => {
    const filtered = filterUsers(userList)
    if (filtered.length === 0) return null
    return (
      <div key={label}>
        <h3 className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase bg-gray-50 sticky top-0 z-10">
          {label}
        </h3>
        <div className="divide-y divide-gray-100">{filtered.map(renderUserRow)}</div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[85dvh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-purple-600" />
            <h2 className="text-lg font-semibold text-gray-900 text-balance">New Group Message</h2>
          </div>
          <Button onClick={handleClose} variant="ghost" size="icon" aria-label="Close">
            <X className="w-5 h-5" />
          </Button>
        </div>

        <div className="p-4 border-b border-gray-200">
          <Input
            type="text"
            placeholder='Group title (e.g. "Level 3 Parents")'
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full"
          />
        </div>

        <div className="px-4 pt-3 border-b border-gray-200 pb-0">
          <Tabs
            value={activeTab}
            onValueChange={(val) => {
              setActiveTab(val)
              setSearchTerm('')
            }}
          >
            <TabsList className="w-full">
              <TabsTrigger value="by-class" className="flex-1 gap-1.5">
                <BookOpen className="w-3.5 h-3.5" />
                By Class
              </TabsTrigger>
              <TabsTrigger value="by-individual" className="flex-1 gap-1.5">
                <Users className="w-3.5 h-3.5" />
                By Individual
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        <div className="p-3 border-b border-gray-200">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              type="text"
              placeholder={activeTab === 'by-class' ? 'Search classes...' : 'Search people...'}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {(activeTab === 'by-class' ? isLoadingKlasses : isLoading) ? (
            <div className="flex items-center justify-center p-8">
              <LoadingSpinner size="8" color="purple" />
            </div>
          ) : activeTab === 'by-class' ? (
            filteredKlasses.length === 0 ? (
              <div className="flex items-center justify-center p-8 text-center">
                <p className="text-sm text-gray-500">No classes found</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {filteredKlasses.map((klass) => {
                  const attrs = klass.attributes || {}
                  const isSelected = selectedKlass?.id === klass.id
                  const classLabel = `${attrs.klassCount} class${attrs.klassCount !== 1 ? 'es' : ''}`
                  const curriculumSummary = attrs.curriculumNames?.length > 0 ? attrs.curriculumNames.join(', ') : null

                  return (
                    <button
                      key={klass.id}
                      onClick={() => handleSelectKlass(klass)}
                      className={`w-full p-4 text-left transition-colors flex items-center gap-3 ${
                        isSelected ? 'bg-purple-50' : 'hover:bg-gray-50'
                      }`}
                    >
                      <div
                        className={`size-9 rounded-full flex items-center justify-center flex-shrink-0 ${
                          isSelected ? 'bg-purple-200' : 'bg-gray-100'
                        }`}
                      >
                        <BookOpen className={`w-4 h-4 ${isSelected ? 'text-purple-600' : 'text-gray-500'}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 text-sm truncate">{attrs.name}</p>
                        <p className="text-xs text-gray-500 truncate">
                          {attrs.facilityName} · {classLabel} ·{' '}
                          <span className="tabular-nums">{attrs.guardianCount}</span> guardian
                          {attrs.guardianCount !== 1 ? 's' : ''}
                        </p>
                        {curriculumSummary && <p className="text-xs text-gray-400 truncate">{curriculumSummary}</p>}
                      </div>
                      {isSelected && <Check className="w-4 h-4 text-purple-600 flex-shrink-0" />}
                    </button>
                  )
                })}
              </div>
            )
          ) : (
            <>
              {renderUserSection(messageableUsers.directors, 'Directors')}
              {renderUserSection(messageableUsers.staff, 'Staff')}
              {renderUserSection(messageableUsers.guardians, 'Guardians')}
              {filterUsers(messageableUsers.directors).length === 0 &&
                filterUsers(messageableUsers.staff).length === 0 &&
                filterUsers(messageableUsers.guardians).length === 0 && (
                  <div className="flex items-center justify-center p-8 text-center">
                    <p className="text-sm text-gray-500">No people found</p>
                  </div>
                )}
            </>
          )}
        </div>

        {activeTab === 'by-individual' && selectedUserIds.length > 0 && (
          <div className="px-4 py-2 border-t border-gray-200 bg-gray-50">
            <p className="text-xs text-gray-500 tabular-nums">
              {selectedUserIds.length} recipient{selectedUserIds.length !== 1 ? 's' : ''} selected
            </p>
          </div>
        )}

        {activeTab === 'by-class' && selectedKlass && (
          <div className="px-4 py-2 border-t border-gray-200 bg-purple-50">
            <p className="text-xs text-purple-700 text-pretty">
              Will message all{' '}
              <span className="font-semibold tabular-nums">{selectedKlass.attributes.guardianCount}</span> guardian
              {selectedKlass.attributes.guardianCount !== 1 ? 's' : ''} enrolled in{' '}
              <span className="font-semibold">{selectedKlass.attributes.name}</span>
              {selectedKlass.attributes.klassCount > 1 && (
                <>
                  {' '}
                  across <span className="font-semibold tabular-nums">{selectedKlass.attributes.klassCount}</span>{' '}
                  classes
                </>
              )}
            </p>
          </div>
        )}

        <div className="flex items-center justify-end gap-2 p-4 border-t border-gray-200">
          <Button onClick={handleClose} variant="outline" disabled={isCreating}>
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            disabled={!canCreate || isCreating}
            className="bg-purple-600 hover:bg-purple-700"
          >
            {isCreating ? 'Creating...' : 'Create Group'}
          </Button>
        </div>
      </div>
    </div>
  )
}

NewGroupConversationModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onCreateGroupConversation: PropTypes.func.isRequired
}

export default NewGroupConversationModal
